import { LUMA } from "./contrast";
import { clampRgba, type Rgba } from "./rgba";
import { apply, fromLinear, type Matrix, toLinear } from "./spaces";

// In linear light, which is where the model is derived and where a mix of two lights is the sum of the two: run over
// the gamma-encoded channels instead and every simulated colour comes out darker than the eye it stands in for.
export function simulate(colour: Rgba, matrix: Matrix | null): Rgba {
  if (!matrix) return colour;
  return clampRgba({ ...fromLinear(apply(matrix, toLinear(colour))), a: colour.a });
}

// Machado, Oliveira and Fernandes (2009), at the full severity of their model.
const PROTANOPIA: Matrix = [
  [0.152286, 1.052583, -0.204868],
  [0.114503, 0.786281, 0.099216],
  [-0.003882, -0.048116, 1.051998],
];

const DEUTERANOPIA: Matrix = [
  [0.367322, 0.860646, -0.227968],
  [0.280085, 0.672501, 0.047413],
  [-0.01182, 0.04294, 0.968881],
];

const TRITANOPIA: Matrix = [
  [1.255528, -0.076749, -0.178779],
  [-0.078411, 0.930809, 0.147602],
  [0.004733, 0.691367, 0.3039],
];

// The one of the four that is not Machado's: every channel comes out as the luminance WCAG weighs a colour by, which
// is what makes a pair differing in hue alone collapse here and nowhere else.
const ACHROMATOPSIA: Matrix = [LUMA, LUMA, LUMA];

export interface Vision {
  id: string;
  label: string;
  // What the missing cone does to the colours, which is the whole of why anybody reads the row.
  note: string;
  // Nothing at all for typical vision: a colour left alone and a colour through an identity matrix differ by the
  // rounding of the trip out through the gamma curve and back, and a swatch that moved is one somebody has to explain.
  matrix: Matrix | null;
}

// The three dichromacies are the far end of the anomalies they each shade into, so a pair that survives these
// survives protanomaly, deuteranomaly and tritanomaly as well, and four tiles say what eight would have said twice.
export const VISIONS: Vision[] = [
  { id: "typical", label: "Typical", note: "All three cone types", matrix: null },
  { id: "protanopia", label: "Protanopia", note: "No long-wave cone, so red darkens", matrix: PROTANOPIA },
  { id: "deuteranopia", label: "Deuteranopia", note: "No middle-wave cone, so red meets green", matrix: DEUTERANOPIA },
  { id: "tritanopia", label: "Tritanopia", note: "No short-wave cone, so blue meets yellow", matrix: TRITANOPIA },
  { id: "achromatopsia", label: "Achromatopsia", note: "No hue at all, only lightness", matrix: ACHROMATOPSIA },
];
